import type { PayloadAction } from "@reduxjs/toolkit"
import { createSelector, createSlice } from "@reduxjs/toolkit"

import {
  billingApi,
  type BillingSummary,
  type BillingUsageSnapshot,
} from "@/api/billingApi"
import type { BillingPlan, BillingPlanId } from "@/consts/billing"
import { logger } from "@/lib/logger"
import { setUser, updateUser } from "@/store/currentUser/slice"
import { logoutAction } from "@/store/session/logoutAction"
import type { AppDispatch, RootState } from "@/store/store"

interface BillingState {
  plan: BillingSummary["plan"] | null
  tariffCreditsLeft: number | null
  extraCredits: number | null
  usageLastFetchedAt: number | null
  usageLastFetchFailed: boolean
  isFetchingUsage: boolean
  plans: BillingPlan[]
  plansLastFetchedAt: number | null
  isFetchingPlans: boolean
  summary: BillingSummary | null
  summaryLastFetchedAt: number | null
  isFetchingSummary: boolean
}

const initialState: BillingState = {
  plan: null,
  tariffCreditsLeft: null,
  extraCredits: null,
  usageLastFetchedAt: null,
  usageLastFetchFailed: false,
  isFetchingUsage: false,
  plans: [],
  plansLastFetchedAt: null,
  isFetchingPlans: false,
  summary: null,
  summaryLastFetchedAt: null,
  isFetchingSummary: false,
}

const PLANS_STALE_AFTER_MS = 5 * 60 * 1000
const SUMMARY_STALE_AFTER_MS = 60 * 1000
export const BILLING_USAGE_STALE_AFTER_MS = 15 * 1000

interface UsagePayload {
  plan: BillingPlanId
  tariffCreditsLeft: number
  extraCredits: number
}
const applyPlanUsage = (
  state: BillingState,
  payload: UsagePayload,
  options?: {
    markUsageFetched?: boolean
  },
) => {
  state.plan = payload.plan
  state.tariffCreditsLeft = payload.tariffCreditsLeft
  state.extraCredits = payload.extraCredits

  if (options?.markUsageFetched) {
    state.usageLastFetchedAt = Date.now()
    state.usageLastFetchFailed = false
  }
}

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    setPlans: (state, action: PayloadAction<BillingPlan[]>) => {
      state.plans = action.payload
      state.plansLastFetchedAt = Date.now()
    },
    setIsFetchingPlans: (state, action: PayloadAction<boolean>) => {
      state.isFetchingPlans = action.payload
    },
    setSummary: (state, action: PayloadAction<BillingSummary>) => {
      state.summary = action.payload
      state.summaryLastFetchedAt = Date.now()
    },
    setIsFetchingSummary: (state, action: PayloadAction<boolean>) => {
      state.isFetchingSummary = action.payload
    },
    setPlanAndTariffCredits: (state, action: PayloadAction<UsagePayload>) => {
      applyPlanUsage(state, action.payload, { markUsageFetched: true })
    },
    setIsFetchingUsage: (state, action: PayloadAction<boolean>) => {
      state.isFetchingUsage = action.payload
    },
    setUsageLastFetchFailed: (state, action: PayloadAction<boolean>) => {
      state.usageLastFetchFailed = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setUser, (state, action) => {
      state.plan = action.payload?.billingPlanId ?? null
    })
    builder.addCase(updateUser, (state, action) => {
      if ("billingPlanId" in action.payload) {
        state.plan = action.payload.billingPlanId ?? null
      }
    })
    builder.addCase(logoutAction, () => initialState)
  },
})

export const {
  setPlans,
  setIsFetchingPlans,
  setSummary,
  setIsFetchingSummary,
  setPlanAndTariffCredits,
  setIsFetchingUsage,
  setUsageLastFetchFailed,
} = billingSlice.actions

export const selectBillingPlan = (state: RootState) =>
  state.billing.plan ?? state.currentUser.user?.billingPlanId ?? null
const selectBillingTariffCreditsLeft = (state: RootState) =>
  state.billing.tariffCreditsLeft
const selectBillingExtraCreditsLeft = (state: RootState) =>
  state.billing.extraCredits
export const selectBillingUsageLastFetchedAt = (state: RootState) =>
  state.billing.usageLastFetchedAt
export const selectIsBillingUsageFailed = (state: RootState) =>
  state.billing.usageLastFetchFailed
export const selectIsBillingUsageFetching = (state: RootState) =>
  state.billing.isFetchingUsage
export const selectBillingPlans = (state: RootState) => state.billing.plans
export const selectIsBillingPlansFetching = (state: RootState) =>
  state.billing.isFetchingPlans
export const selectBillingSummary = (state: RootState) => state.billing.summary
export const selectIsBillingSummaryFetching = (state: RootState) =>
  state.billing.isFetchingSummary

const selectBillingUsage = createSelector(
  [
    selectBillingSummary,
    selectBillingPlan,
    selectBillingPlans,
    selectBillingTariffCreditsLeft,
    selectBillingExtraCreditsLeft,
  ],
  (
    summary,
    planId,
    plans,
    tariffCreditsLeft,
    extraCredits,
  ): BillingUsageSnapshot | null => {
    const usage = summary?.usage ?? null
    const currentPlan = plans.find((p) => p.id === planId)
    const rawCredits = currentPlan?.features?.ai_credits
    const fallbackTotal = typeof rawCredits === "number" ? rawCredits : 0

    if (usage) {
      return {
        ...usage,
        tariffCreditsLeft: tariffCreditsLeft ?? usage.tariffCreditsLeft,
        extraCredits: extraCredits ?? usage.extraCredits,
      }
    }

    return {
      tariffCreditsTotal: fallbackTotal,
      tariffCreditsLeft: tariffCreditsLeft ?? 0,
      extraCredits: extraCredits ?? 0,
      resetsAt: null,
      breakdown: {},
    }
  },
)

export interface BillingPlanUsageSnapshot {
  plan: BillingSummary["plan"] | null
  usage: BillingUsageSnapshot | null
}

export const selectBillingPlanUsage = createSelector(
  [selectBillingPlan, selectBillingUsage],
  (plan, usage): BillingPlanUsageSnapshot => ({
    plan,
    usage,
  }),
)

const isStale = (lastFetchedAt: number | null, ttlMs: number): boolean => {
  if (lastFetchedAt === null) return true
  return Date.now() - lastFetchedAt > ttlMs
}

interface FetchOptions {
  force?: boolean
}

let billingPlansRequest: Promise<BillingPlan[]> | null = null
let billingSummaryRequest: Promise<BillingSummary | null> | null = null
let billingUsageRequest: Promise<BillingPlanUsageSnapshot> | null = null

export const ensureBillingPlans =
  (options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<BillingPlan[]> => {
    const state = getState().billing

    if (
      !options.force &&
      !isStale(state.plansLastFetchedAt, PLANS_STALE_AFTER_MS) &&
      state.plans.length
    ) {
      return state.plans
    }

    if (state.isFetchingPlans && billingPlansRequest) {
      return billingPlansRequest
    }

    dispatch(setIsFetchingPlans(true))

    billingPlansRequest = (async () => {
      try {
        const plans = await billingApi.getPlans()
        dispatch(setPlans(plans))
        return plans
      } catch (error) {
        logger.error("[billingSlice] Failed to fetch plans", error)
        throw error
      } finally {
        dispatch(setIsFetchingPlans(false))
        billingPlansRequest = null
      }
    })()

    return billingPlansRequest
  }

export const ensureBillingSummary =
  (options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<BillingSummary | null> => {
    const state = getState().billing

    if (
      !options.force &&
      !isStale(state.summaryLastFetchedAt, SUMMARY_STALE_AFTER_MS) &&
      state.summary
    ) {
      logger.log(
        "[billingSlice] recordingCount ensureBillingSummary skipped: fresh cache",
      )
      return state.summary
    }

    if (!options.force && state.isFetchingSummary && billingSummaryRequest) {
      logger.log(
        "[billingSlice] recordingCount ensureBillingSummary skipped: request in flight",
      )
      return billingSummaryRequest
    }

    dispatch(setIsFetchingSummary(true))
    logger.log("[billingSlice] recordingCount ensureBillingSummary: fetching", {
      force: options.force ?? false,
    })

    billingSummaryRequest = (async () => {
      try {
        const summary = await billingApi.getSummary()
        dispatch(setSummary(summary))
        return summary
      } catch (error) {
        logger.error("[billingSlice] Failed to fetch summary", error)
        throw error
      } finally {
        dispatch(setIsFetchingSummary(false))
        billingSummaryRequest = null
      }
    })()

    return billingSummaryRequest
  }

export const ensureBillingUsage =
  (options: FetchOptions = {}) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<BillingPlanUsageSnapshot> => {
    const state = getState().billing
    const cachedPlanUsage = selectBillingPlanUsage(getState())

    if (
      !options.force &&
      !isStale(state.usageLastFetchedAt, BILLING_USAGE_STALE_AFTER_MS) &&
      (cachedPlanUsage.plan !== null || cachedPlanUsage.usage !== null)
    ) {
      return cachedPlanUsage
    }

    if (!options.force && state.isFetchingUsage && billingUsageRequest) {
      return billingUsageRequest
    }

    dispatch(setIsFetchingUsage(true))
    dispatch(setUsageLastFetchFailed(false))

    void dispatch(ensureBillingSummary())

    billingUsageRequest = (async () => {
      try {
        const usage = await billingApi.checkCredits()
        dispatch(setPlanAndTariffCredits(usage))
        return selectBillingPlanUsage(getState())
      } catch (error) {
        logger.error("[billingSlice] Failed to fetch billing usage", error)
        dispatch(setUsageLastFetchFailed(true))
        throw error
      } finally {
        dispatch(setIsFetchingUsage(false))
        billingUsageRequest = null
      }
    })()

    return billingUsageRequest
  }

export default billingSlice.reducer
